import { gql } from '@apollo/client';

import { CORE_ASSET_FIELDS } from 'app/src/components/Assets/queries';
import {
  SALE_FIELDS as SALE_CORE_FIELDS,
} from 'app/src/components/Sales/queries';
import {
  SALE_ASSET_CORE_FIELDS,
  SALE_ASSET_PRE_BID_FIELDS,
} from 'app/src/components/Sales/Process/queries';

export const BID_CORE_FIELDS = gql`
  fragment BidCoreFields on Bid {
    id
    price
    note
    paddleNumber
    createdAt
    removedAt
  }
`;

const SALE_ASSET_BIDDING_FIELDS = gql`
  ${SALE_ASSET_CORE_FIELDS}
  fragment SaleAssetBiddingFields on SaleAsset {
    ...SaleAssetCoreFields
    biddingNext
    biddingSoldAmount
    biddingSoldBuyerDetails
    pendingSellerConfirmation
    pendingBidGroupId
    highBid
    asset {
      id
      name
      vin
      cityState
      primaryPhotoUrl
      webPath
      announcements {
        id
        body
        acceptances {
          id
          buyerUser {
            id
          }
        }
      }
    }
  }
`;

// NOTE (Jared E Morgan, 2024-02-27) - THIS IS IMPORTANT!
// bidGroupSaleAssets MUST be above biddingSaleAsset in the list of fields in
// this fragment. useSubscription(BIDDING_UPDATED) updates only the
// biddingSaleAsset, but if this sale asset is part of a bidding group, the
// cache update sees the old information from bidGroupSaleAssets and reverts the
// data back to the old information. By having the cache process the old data
// first, the biddingSaleAsset updates win out due to being the last update.
// TODO Actually update the grouped sale asset in useSubscriptions so this is
// not a problem.
const BIDDING_SALE_FIELDS = gql`
  ${BID_CORE_FIELDS}
  ${SALE_ASSET_BIDDING_FIELDS}
  ${SALE_ASSET_PRE_BID_FIELDS}
  fragment BiddingSaleFields on Sale {
    bidGroupSaleAssets {
      ...SaleAssetBiddingFields
    }
    ${SALE_CORE_FIELDS}
    lotsRemainingLabel
    lotsSoldCount
    biddingBuyerBackgroundColor
    biddingBuyerForegroundColor
    biddingSaleAsset {
      ...SaleAssetBiddingFields
      ...SaleAssetPreBidFields
      highBid
      nextRemovedBid { id }
      bids {
        ...BidCoreFields
        buyer { id, city, state, cityState }
        backgroundColor
        foregroundColor
      }
    }
    priorSaleAsset {
      ...SaleAssetBiddingFields
    }
    activeStream
    auctioneerRoomCode
    viewerRoomCode
    activeBidderCount
  }
`;

export const AUDIO_STREAM_CONFIG = gql`
  query AudioStreamConfig {
    audioStreamConfig {
      accessKeyId
      secretAccessKey
      channelArn
      storageChannelArn
      region
    }
  }
`;

export const VIDEO_STREAM_CORE_FIELDS = gql`
  fragment VideoStreamCoreFields on VideoStream {
    id
    startAt
    checkInAt
    endAt
    savingStartedAt
    savingEndedAt
  }
`;

const VIDEO_STREAM_FIELDS = gql`
  ${VIDEO_STREAM_CORE_FIELDS}
  fragment VideoStreamFields on VideoStream {
    ...VideoStreamCoreFields
    sale { id, name }
    originalId
  }
`;

export const SALE_VIDEO_STREAMS = gql`
  ${VIDEO_STREAM_CORE_FIELDS}
  query SaleVideoStreams($id: ID!) {
    sale(id: $id) {
      id
      name
      saleRecordings {
        id
        startAt
        endAt
        url
      }
      videoStreams {
        ...VideoStreamCoreFields
        video {
          id
          filename
          contentType
          url
        }
      }
    }
  }
`;

export const START_VIDEO_STREAM = gql`
  ${VIDEO_STREAM_FIELDS}
  mutation StartVideoStream($saleId: ID!) {
    startVideoStream(saleId: $saleId) {
      videoStream {
        ...VideoStreamFields
      }
      errors { path, message }
    }
  }
`;

export const END_VIDEO_STREAM = gql`
  ${VIDEO_STREAM_FIELDS}
  mutation EndVideoStream($id: ID!) {
    endVideoStream(id: $id) {
      videoStream {
        ...VideoStreamFields
      }
      errors { path, message }
    }
  }
`;

export const VIDEO_STREAM_CHECK_IN = gql`
  mutation VideoStreamCheckIn($id: ID!) {
    videoStreamCheckIn(id: $id) {
      errors { path, message }
    }
  }
`;

export const UPDATE_SALE_BIDDING = gql`
  ${BIDDING_SALE_FIELDS}
  mutation UpdateSaleBidding(
    $id: ID!
    $bidding: Boolean
    $biddingSaleAssetId: ID
    $biddingNotice: String
  ) {
    updateSaleBidding(
      id: $id
      bidding: $bidding
      biddingSaleAssetId: $biddingSaleAssetId
      biddingNotice: $biddingNotice
    ) {
      sale {
         ...BiddingSaleFields
      }
      errors { path, message }
    }
  }
`;

export const SALE = gql`
  ${BIDDING_SALE_FIELDS}
  query Sale($id: ID!) {
    sale(id: $id) {
      ...BiddingSaleFields
    }
  }
`;

export const SALE_UPDATED = gql`
  ${BIDDING_SALE_FIELDS}
  subscription SaleUpdated($id: ID!) {
    saleUpdated(id: $id) {
      sale {
        ...BiddingSaleFields
      }
    }
  }
`;

export const BIDDING_SALE_ASSETS = gql`
  ${SALE_ASSET_BIDDING_FIELDS}
  query BiddingSaleAssets(
    $saleId: ID
  ) {
    saleAssets(
      saleId: $saleId
    ) {
      ...SaleAssetBiddingFields
    }
  }
`;

export const BIDDING_SALE_ASSET_UPDATED = gql`
  ${SALE_ASSET_BIDDING_FIELDS}
  subscription BiddingSaleAssetUpdated($saleId: ID!) {
    saleAssetUpdated(saleId: $saleId, scope: "bidding") {
      saleAsset {
        ...SaleAssetBiddingFields
      }
    }
  }
`;

export const BIDDING_UPDATED = gql`
  ${BID_CORE_FIELDS}
  subscription BiddingUpdated($saleId: ID!) {
    biddingUpdated(saleId: $saleId) {
      saleAssetId
      biddingNextUpdated
      biddingNext
      biddingNextManual
      biddingStep

      bidsUpdated
      nextRemovedBid { id }
      highBid
      bids {
        ...BidCoreFields
        buyer { id, city, state, cityState }
        backgroundColor
        foregroundColor
      }

      biddingFlashUpdated
      biddingFlash
      biddingFlashBackgroundColor
      biddingFlashForegroundColor
      biddingBuyerBackgroundColor
      biddingBuyerForegroundColor
      biddingFlashFades
      biddingFlashType

      biddingStatusUpdated
      biddingStatus

      biddingNoticeUpdated
      biddingNotice

      audioStreamUpdated
      activeStream
    }
  }
`;

export const CREATE_BID = gql`
  ${BID_CORE_FIELDS}
  mutation CreateBid(
    $saleAssetId: ID!
    $price: Decimal!
    $note: String
  ) {
    createBid(
      saleAssetId: $saleAssetId
      price: $price
      note: $note
    ) {
      bid {
        ...BidCoreFields
      }
      errors { path, message }
    }
  }
`;

export const BIDDING_ASSET = gql`
  query Asset($id: ID!) {
    asset(id: $id) {
      ${CORE_ASSET_FIELDS}
      cityState
      generalDetails
      photos {
        id
        filename
        contentType
        url
      }
    }
  }
`;

export const BID_WON = gql`
  mutation BidWon(
    $saleAssetId: ID!
    $isIf: Boolean
  ) {
    bidWon(
      saleAssetId: $saleAssetId
      isIf: $isIf
    ) {
      errors { path, message }
    }
  }
`;

export const BIDDING_PASS = gql`
  mutation BiddingPass(
    $saleAssetId: ID!
  ) {
    biddingPass(
      saleAssetId: $saleAssetId
    ) {
      errors { path, message }
    }
  }
`;

export const REMOVE_LAST_BID = gql`
  mutation RemoveLastBid(
    $saleAssetId: ID!
  ) {
    removeLastBid(
      saleAssetId: $saleAssetId
    ) {
      errors { path, message }
    }
  }
`;

export const UNDO_REMOVE_LAST_BID = gql`
  mutation UndoRemoveLastBid(
    $saleAssetId: ID!
  ) {
    undoRemoveLastBid(
      saleAssetId: $saleAssetId
    ) {
      errors { path, message }
    }
  }
`;

export const CREATE_BID_GROUP = gql`
  mutation CreateBidGroup($saleAssetIds: [ID!]!) {
    createBidGroup(saleAssetIds: $saleAssetIds) {
      errors { path, message }
    }
  }
`;

export const UPDATE_BID_GROUP = gql`
  mutation UpdateBidGroup(
    $id: ID!
    $saleAssetIds: [ID!]!
  ) {
    updateBidGroup(
      id: $id
      saleAssetIds: $saleAssetIds
    ) {
      errors { path, message }
    }
  }
`;

export const CHOOSE_BID_GROUP = gql`
  mutation ChooseBidGroup(
    $id: ID!
    $saleAssetIds: [ID!]!
  ) {
    chooseBidGroup(
      id: $id
      saleAssetIds: $saleAssetIds
    ) {
      errors { path, message }
    }
  }
`;

export const START_BIDDING_VISIT = gql`
  mutation StartBiddingVisit(
    $saleId: ID!
    $page: String!
  ) {
    startBiddingVisit(
      saleId: $saleId
      page: $page
    ) {
      biddingVisit { id }
      errors { path, message }
    }
  }
`;

export const END_BIDDING_VISIT = gql`
  mutation EndBiddingVisit($id: ID!) {
    endBiddingVisit(id: $id) {
      biddingVisit { id }
      errors { path, message }
    }
  }
`;

export const BIDDING_VISIT_CHECK_IN = gql`
  mutation BiddingVisitCheckIn(
    $id: ID!
    $audio: Boolean!
  ) {
    biddingVisitCheckIn(
      id: $id
      audio: $audio
    ) {
      errors { path, message }
    }
  }
`;

export const REACTIVATE_BIDDING = gql`
  mutation ReactivateBiddingSaleAsset($id: ID!) {
    reactivateBiddingSaleAsset(id: $id) {
      errors { path, message }
    }
  }
`;

export const CREATE_BID_FROM_PRE_BID = gql`
  ${BID_CORE_FIELDS}
  mutation CreateBidFromPreBid(
    $preBidId: ID!
    $price: Int!
  ) {
    createBidFromPreBid(
      preBidId: $preBidId
      price: $price
    ) {
      bid {
        ...BidCoreFields
      }
      errors { path, message }
    }
  }
`;
